import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../components/ui/select";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { 
  Users, 
  UserPlus, 
  Edit, 
  Trash2, 
  Shield, 
  Eye,
  EyeOff 
} from "lucide-react";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [roleFilter, setRoleFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "",
    permissions: {},
    status: "active"
  });

  const roleOptions = [
    { value: "super_admin", label: "Super Admin", labelSo: "Maamule Guud" },
    { value: "admin", label: "Admin", labelSo: "Maamule" },
    { value: "academic_admin", label: "Academic Admin", labelSo: "Maamule Waxbarasho" },
    { value: "finance_admin", label: "Finance Admin", labelSo: "Maamule Maaliyadeed" },
    { value: "transport_admin", label: "Transport Admin", labelSo: "Maamule Gaadiid" },
    { value: "teacher", label: "Teacher", labelSo: "Macalin" }
  ];

  const permissionOptions = [
    { key: "students", label: "Students", labelSo: "Ardayda" },
    { key: "teachers", label: "Teachers", labelSo: "Macallimiinta" },
    { key: "classes", label: "Classes", labelSo: "Fasallada" },
    { key: "attendance", label: "Attendance", labelSo: "Soo-joogga" },
    { key: "assessments", label: "Assessments", labelSo: "Imtixaannada" },
    { key: "finance", label: "Finance", labelSo: "Maaliyadda" },
    { key: "transport", label: "Transport", labelSo: "Gaadiidka" },
    { key: "messages", label: "Messages", labelSo: "Farriimaha" },
    { key: "reports", label: "Reports", labelSo: "Warbixinnada" },
    { key: "settings", label: "Settings", labelSo: "Dejinta" }
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to load users");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users`, formData);
      toast.success("✅ User created successfully");
      setIsAddModalOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to create user";
      toast.error(errorMessage);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }
      
      await axios.put(`${API}/users/${selectedUser.id}`, updateData);
      toast.success("✅ User updated successfully");
      setIsEditModalOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to update user";
      toast.error(errorMessage);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("✅ User deleted successfully");
      fetchUsers();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to delete user";
      toast.error(errorMessage);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name || "",
      email: user.email || "",
      password: "",
      role: user.role || "",
      permissions: user.permissions || {},
      status: user.status || "active"
    });
    setIsEditModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      password: "",
      role: "",
      permissions: {},
      status: "active"
    });
    setSelectedUser(null);
    setShowPassword(false);
  };

  const handleRoleChange = (role) => {
    // Get default permissions for the selected role
    const defaultPermissions = getDefaultPermissions(role);
    setFormData({
      ...formData,
      role,
      permissions: defaultPermissions
    });
  };

  const getDefaultPermissions = (role) => {
    const permissions = {
      students: false,
      teachers: false,
      classes: false,
      attendance: false,
      assessments: false,
      finance: false,
      transport: false,
      messages: false,
      reports: false,
      settings: false
    };

    switch (role) {
      case "super_admin":
        return Object.keys(permissions).reduce((acc, key) => ({ ...acc, [key]: true }), {});
      case "admin":
        return { ...permissions, students: true, teachers: true, classes: true, attendance: true, assessments: true, finance: true, transport: true, messages: true, reports: true };
      case "academic_admin":
        return { ...permissions, students: true, teachers: true, classes: true, attendance: true, assessments: true, messages: true, reports: true };
      case "finance_admin":
        return { ...permissions, students: true, finance: true, messages: true, reports: true };
      case "transport_admin":
        return { ...permissions, students: true, transport: true, messages: true, reports: true };
      case "teacher":
        return { ...permissions, classes: true, attendance: true, assessments: true, messages: true };
      default:
        return permissions;
    }
  };

  const handlePermissionChange = (permission, checked) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [permission]: checked
      }
    });
  };

  const filteredUsers = users.filter(user => {
    const matchesRole = roleFilter === "all" || user.role === roleFilter;
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesRole && matchesSearch;
  });

  const getRoleLabel = (role) => {
    const roleOption = roleOptions.find(r => r.value === role);
    return roleOption ? roleOption.label : role;
  };

  const getStatusBadge = (status) => {
    return (
      <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full font-medium ${
        status === 'active' 
          ? 'bg-green-100 text-green-700' 
          : 'bg-red-100 text-red-700'
      }`}>
        {status === 'active' ? '✓ Active' : '⚪ Inactive'}
      </span>
    );
  };

  const activeUsersCount = users.filter(u => u.status === 'active').length;
  const rolesCount = [...new Set(users.map(u => u.role))].length;

  return (
    <div className="space-y-6" data-testid="user-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1E624A]">User Management</h1>
          <p className="text-gray-600 text-sm mt-1">Maamulka Isticmaalayaasha</p>
        </div>
        <Button
          onClick={() => setIsAddModalOpen(true)}
          className="bg-[#1E624A] hover:bg-[#164935]"
          data-testid="add-user-btn"
        >
          <UserPlus size={16} className="mr-2" />
          Add New User
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg p-6 shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-[#1E624A]">{users.length}</p>
            </div>
            <Users size={24} className="text-[#1E624A]" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-6 shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Users</p>
              <p className="text-2xl font-bold text-green-600">{activeUsersCount}</p>
            </div>
            <Shield size={24} className="text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-6 shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Different Roles</p>
              <p className="text-2xl font-bold text-blue-600">{rolesCount}</p>
            </div>
            <Users size={24} className="text-blue-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 bg-white p-4 rounded-lg shadow-md">
        <div className="flex-1">
          <Input
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="user-search"
          />
        </div>
        <div className="w-full sm:w-48">
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {roleOptions.map(role => (
                <SelectItem key={role.value} value={role.value}>
                  {role.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Role</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Permissions</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    Loading users...
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">
                      {user.name}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      {user.email}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full font-medium ${
                        user.role === 'super_admin' ? 'bg-purple-100 text-purple-700' :
                        user.role === 'admin' ? 'bg-blue-100 text-blue-700' :
                        user.role === 'teacher' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      {getStatusBadge(user.status)}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <div className="flex flex-wrap gap-1">
                        {user.permissions && Object.entries(user.permissions)
                          .filter(([key, value]) => value)
                          .slice(0, 3)
                          .map(([key]) => (
                            <span key={key} className="inline-flex items-center px-2 py-0.5 text-xs rounded bg-[#1E624A] text-white">
                              {permissionOptions.find(p => p.key === key)?.label || key}
                            </span>
                          ))}
                        {user.permissions && Object.entries(user.permissions).filter(([key, value]) => value).length > 3 && (
                          <span className="text-xs text-gray-500">
                            +{Object.entries(user.permissions).filter(([key, value]) => value).length - 3} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex justify-center space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditModal(user)}
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Edit size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteUser(user.id)}
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#1E624A]">Add New User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="add-user-name"
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  data-testid="add-user-email"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="password">Password *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required
                    data-testid="add-user-password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="role">Role *</Label>
                <Select value={formData.role} onValueChange={handleRoleChange} required>
                  <SelectTrigger data-testid="add-user-role">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roleOptions.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Permissions */}
            <div>
              <Label>Permissions</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2 p-4 border rounded-lg bg-gray-50">
                {permissionOptions.map(permission => (
                  <label key={permission.key} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.permissions[permission.key] || false}
                      onChange={(e) => handlePermissionChange(permission.key, e.target.checked)}
                      className="w-4 h-4 text-[#1E624A] bg-gray-100 border-gray-300 rounded focus:ring-[#1E624A]"
                      data-testid={`add-user-permission-${permission.key}`}
                    />
                    <span className="text-sm">{permission.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsAddModalOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#1E624A] hover:bg-[#164935]"
                data-testid="add-user-submit"
              >
                Create User
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#1E624A]">Edit User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditUser} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-name">Full Name *</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="edit-user-name"
                />
              </div>
              <div>
                <Label htmlFor="edit-email">Email *</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  data-testid="edit-user-email"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-password">New Password (leave blank to keep current)</Label>
                <div className="relative">
                  <Input
                    id="edit-password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    data-testid="edit-user-password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="edit-role">Role *</Label>
                <Select value={formData.role} onValueChange={handleRoleChange} required>
                  <SelectTrigger data-testid="edit-user-role">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roleOptions.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="edit-status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                <SelectTrigger data-testid="edit-user-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Permissions */}
            <div>
              <Label>Permissions</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2 p-4 border rounded-lg bg-gray-50">
                {permissionOptions.map(permission => (
                  <label key={permission.key} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.permissions[permission.key] || false}
                      onChange={(e) => handlePermissionChange(permission.key, e.target.checked)}
                      className="w-4 h-4 text-[#1E624A] bg-gray-100 border-gray-300 rounded focus:ring-[#1E624A]"
                      data-testid={`edit-user-permission-${permission.key}`}
                    />
                    <span className="text-sm">{permission.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsEditModalOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#1E624A] hover:bg-[#164935]"
                data-testid="edit-user-submit"
              >
                Update User
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;